import * as React from 'react'

import { MobXProviderContext, observer } from 'mobx-react'

import CursorFollowTooltip from '~/client/src/shared/components/CursorFollowTooltip/CursorFollowTooltip'
import { ResourceCard } from '~/client/src/shared/components/ResourceCardList/ResourceCard'
import { buildResourceCardModel } from '~/client/src/shared/components/ResourceCardList/resourceCardMapper'
import {
  RESOURCE_ICON_CLASS_NAME,
  useResourceIcon,
} from '~/client/src/shared/components/ResourcePicker/useResourceRenderItem'
import { ResourceTypeId } from '~/client/src/shared/constants/resourceTypes'
import useEquipmentResources from '~/client/src/shared/hooks/useEquipmentResources'
import useLocationResources from '~/client/src/shared/hooks/useLocationResources'
import { useLocationSubtitle } from '~/client/src/shared/hooks/useLocationSubtitle'
import useMaterialResources from '~/client/src/shared/hooks/useMaterialResources'
import { useResourceCardSchema } from '~/client/src/shared/hooks/useResourceCardSchema'
import { useResourceRuntimeContext } from '~/client/src/shared/hooks/useResourceRuntimeContext'
import { IResourceRecord } from '~/client/src/shared/resources/definitions/types'
import type { ResourceSchema } from '~/client/src/shared/resources/domain/resourceSchema'
import { renderResourceRecordIcon } from '~/client/src/shared/resources/icons/resourceRecordIcon'

// `!` beats the popup's legacy 4px radius, which sits outside Tailwind's layers.
export const HOVER_CARD_POPUP_CLASS = 'tw:rounded-lg!'

interface IProps {
  resourceTypeId: string
  recordId: string
  projectId?: string
  /** Opens the resource's card. Omit for a reference that only previews. */
  onOpen?: () => void
  children: React.ReactNode
}

interface IHoverProps extends Omit<IProps, 'recordId' | 'projectId'> {
  record: IResourceRecord | null
  schema: ResourceSchema
}

const ResourceHoverReference: React.FC<IHoverProps> = observer(
  ({ resourceTypeId, record, schema, onOpen, children }) => {
    const runtimeContext = useResourceRuntimeContext()
    const { cardConfiguration, properties, getDisplayValue } =
      useResourceCardSchema({ resourceTypeId, runtimeContext, schema })
    const resolveSubtitle = useLocationSubtitle(resourceTypeId)
    const icon = useResourceIcon(resourceTypeId)

    // Not memoized: the subtitle and display values read stores a memo can't track.
    const tooltipContent = record && (
      <div className="tw:w-[350px]">
        <ResourceCard.Standard
          model={buildResourceCardModel({
            record,
            resourceTypeId,
            cardConfiguration,
            properties,
            getDisplayValue,
            resolveSubtitle: ({ record: item, subtitle }) =>
              resolveSubtitle(item, subtitle),
          })}
          variant="menu"
          leading={renderResourceRecordIcon(
            resourceTypeId,
            record,
            icon,
            RESOURCE_ICON_CLASS_NAME,
          )}
        />
      </div>
    )

    const reference = onOpen ? (
      <span
        role="button"
        tabIndex={0}
        className="tw:group/value tw:inline-flex tw:max-w-full tw:cursor-pointer"
        onClick={onOpen}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            onOpen()
          }
        }}
      >
        {children}
      </span>
    ) : (
      <span className="tw:inline-flex tw:max-w-full">{children}</span>
    )

    // No record to preview (still loading, or gone): no empty popup. On touch a tap would
    // open the preview and leave it stuck over the card the same tap opens.
    const { isMobileEnv } = React.useContext(MobXProviderContext) as {
      isMobileEnv?: boolean
    }
    return tooltipContent && !isMobileEnv ? (
      <CursorFollowTooltip
        content={tooltipContent}
        // With the card's own padding this lands on Figma's 16/18.
        portalClassName={`${HOVER_CARD_POPUP_CLASS} tw:px-2.5 tw:py-2`}
      >
        {reference}
      </CursorFollowTooltip>
    ) : (
      reference
    )
  },
)

function useRecord(records: IResourceRecord[], recordId: string) {
  return React.useMemo(
    () => records.find(item => item.id === String(recordId)) || null,
    [records, recordId],
  )
}

// One component per type: each resource store comes from its own hook, and hooks can't be
// picked conditionally.
const LocationValueReference: React.FC<IProps> = observer(
  ({ projectId, recordId, ...rest }) => {
    const { records, schema } = useLocationResources({ projectId })
    const record = useRecord(records, recordId)
    return <ResourceHoverReference {...rest} record={record} schema={schema} />
  },
)

const EquipmentValueReference: React.FC<IProps> = observer(
  ({ projectId, recordId, ...rest }) => {
    const { records, schema } = useEquipmentResources({ projectId })
    const record = useRecord(records, recordId)
    return <ResourceHoverReference {...rest} record={record} schema={schema} />
  },
)

const MaterialValueReference: React.FC<IProps> = observer(
  ({ projectId, recordId, ...rest }) => {
    const { records, schema } = useMaterialResources({ projectId })
    const record = useRecord(records, recordId)
    return <ResourceHoverReference {...rest} record={record} schema={schema} />
  },
)

/**
 * A resource value that previews its card on hover and, given `onOpen`, opens it on click —
 * the way a linked workflow does.
 */
export const ResourceValueReference: React.FC<IProps> = props => {
  switch (props.resourceTypeId) {
    case ResourceTypeId.LOCATIONS:
      return <LocationValueReference {...props} />
    case ResourceTypeId.OFFLOADING_EQUIPMENT:
      return <EquipmentValueReference {...props} />
    case ResourceTypeId.MATERIALS:
      return <MaterialValueReference {...props} />
    default:
      return <>{props.children}</>
  }
}
